import { z } from 'zod';
import { toCamelCase } from 'utils/strings';

// Account errors keyed by Rails attribute name, where that name diverges from
// this form's own field name. require_full_address is never set on these PAYG
// paths, so account-level address errors stayed dormant until validations
// that don't depend on it (country_code_recognised, and region/postal_code's
// combination checks, which also fire on any new record regardless) started
// reaching real user input here.
const ACCOUNT_ERROR_FIELD_NAMES = {
  country_code: 'country',
  region: 'state',
  postal_code: 'postCode',
};

export function mapAccountErrors(accountErrors = {}) {
  return Object.entries(accountErrors).reduce((acc, [key, value]) => {
    acc[ACCOUNT_ERROR_FIELD_NAMES[key] || toCamelCase(key)] = value;
    return acc;
  }, {});
}

export const initialFormValues = {
  fullName: '',
  email: '',
  password: '',
  billingEmail: '',
  country: { label: '', value: '' },
  addressLineOne: '',
  addressLineTwo: '',
  city: '',
  postCode: '',
  state: { label: '', value: '' },
  companyName: '',
  vatNumber: '',
  acceptsTerms: '',
};

export const signupFormSchema = z.object({
  fullName: z.string().min(1, { message: 'Please enter your full name' }),
  password: z
    .string()
    .trim()
    .min(8, { message: 'Password must be more than 8 character and contain 1 special character' }),
  email: z.string().email({ message: 'The email you entered is not valid' }),
  country: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .refine((_) => _.value.length || _.label.length, { message: 'Please select your country' }),
  addressLineOne: z.string().min(1, { message: 'Please enter your address' }),
  city: z.string().min(1, { message: 'Please enter your city' }),
  postCode: z.string().min(1, { message: 'Please enter your Post Code' }),
  acceptsTerms: z
    .union([z.string(), z.boolean()])
    .refine((val) => Boolean(val), { message: 'Please agree to the terms to continue' }),
});

export const upgradeFormSchema = z.object({
  country: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .refine((_) => _.value.length || _.label.length, { message: 'Please select your country' }),
  addressLineOne: z.string().min(1, { message: 'Please enter your address' }),
  city: z.string().min(1, { message: 'Please enter your city' }),
  postCode: z.string().min(1, { message: 'Please enter your Post Code' }),
  acceptsTerms: z
    .union([z.string(), z.boolean()])
    .refine((val) => Boolean(val), { message: 'Please agree to the terms to continue' }),
});

export const migrateFormSchema = z.object({
  country: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .refine((_) => _.value.length || _.label.length, { message: 'Please select your country' }),
  addressLineOne: z.string().min(1, { message: 'Please enter your address' }),
  city: z.string().min(1, { message: 'Please enter your city' }),
  postCode: z.string().min(1, { message: 'Please enter your Post Code' }),
  packageId: z
    .string({ message: 'Choose a package before migrating' })
    .min(1, { message: 'Choose a package before migrating' }),
});

export function formatZodErrors(errObject) {
  return Object.entries(errObject)
    .filter(([key]) => key !== '_errors')
    .reduce(
      (acc, [name, { _errors }]) => ({
        ...acc,
        [name]: _errors,
      }),
      {},
    );
}

const stateValidation = z
  .object({ value: z.string(), label: z.string() })
  .refine((_) => _.value.length || _.label.length, { message: 'Please select your state' });

export function upgradeFormValidator(values) {
  const schema =
    values.country.value === 'US' ? upgradeFormSchema.extend({ state: stateValidation }) : upgradeFormSchema;
  const data = schema.safeParse(values);
  const formattedErrors = data.error?.format();

  return formattedErrors ? formatZodErrors(formattedErrors) : null;
}

export function migrateFormValidator(values) {
  const schema =
    values.country.value === 'US' ? migrateFormSchema.extend({ state: stateValidation }) : migrateFormSchema;
  const data = schema.safeParse(values);
  const formattedErrors = data.error?.format();

  return formattedErrors ? formatZodErrors(formattedErrors) : null;
}

export function signupFormValidator(values) {
  const schema = values.country.value === 'US' ? signupFormSchema.extend({ state: stateValidation }) : signupFormSchema;

  const data = schema.safeParse(values);

  const formattedErrors = data.error?.format();

  return formattedErrors ? formatZodErrors(formattedErrors) : null;
}

export const stripeElementStyles = { style: { base: { '::placeholder': { color: '#a0aec0', fontWeight: '300' } } } };

export const selectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: 0,
    cursor: 'pointer',
  }),
  indicatorSeparator: () => ({}),
};
